import { useId, useState, type FormEvent } from 'react';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { Card } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { FieldError } from '../../components/ui/FieldError';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { ApiProblemError, postQuote, type Quote } from '../../lib/api-client';
import { QuoteResult } from './components/QuoteResult';
import { useQuoteApiKey } from './useQuoteApiKey';

const DEFAULT_PACKAGE = { height_cm: 20, width_cm: 20, length_cm: 20 };

/** The only `field` values this form has a dedicated slot for — anything else in an RFC 9457 `errors[]` falls back to the general banner below instead of silently disappearing (qa-validator finding, TTP-287). */
const KNOWN_FIELDS = new Set([
  'from_postal_code',
  'to_postal_code',
  'weight_g',
  'height_cm',
  'width_cm',
  'length_cm',
]);

/**
 * `/quotes` (TTP-287) — the demo-quote form/result from onboarding's Tela 4
 * (`DemoStep.tsx`), now reachable at any time from the post-onboarding
 * dashboard instead of once during signup. Same validation, same
 * `QuoteResult` UI (shared component, not duplicated), same sandbox/live key
 * decision — just re-derived from the org's current provider connections
 * (`useQuoteApiKey`) instead of onboarding-only in-memory state.
 *
 * Deliberately stateless across visits (fora do escopo: "não implemente
 * persistência histórica das cotações simuladas") — every mount starts from
 * an empty form.
 */
export function QuotesPage() {
  const fromId = useId();
  const toId = useId();
  const weightId = useId();
  const heightId = useId();
  const widthId = useId();
  const lengthId = useId();

  const { activeKey, isSandbox, loading: keyLoading, error: keyError } = useQuoteApiKey();

  const [fromPostalCode, setFromPostalCode] = useState('');
  const [toPostalCode, setToPostalCode] = useState('');
  const [weightG, setWeightG] = useState('1000');
  const [heightCm, setHeightCm] = useState(String(DEFAULT_PACKAGE.height_cm));
  const [widthCm, setWidthCm] = useState(String(DEFAULT_PACKAGE.width_cm));
  const [lengthCm, setLengthCm] = useState(String(DEFAULT_PACKAGE.length_cm));

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);

  const unmappedFieldErrors = Object.entries(fieldErrors).filter(
    ([field, message]) => Boolean(message) && !KNOWN_FIELDS.has(field),
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const digitsFrom = fromPostalCode.replace(/\D/g, '');
    const digitsTo = toPostalCode.replace(/\D/g, '');
    const weight = Number(weightG);
    const height = Number(heightCm);
    const width = Number(widthCm);
    const length = Number(lengthCm);

    const nextErrors: Record<string, string | undefined> = {};
    if (digitsFrom.length !== 8) nextErrors.from_postal_code = 'CEP de origem deve ter 8 dígitos.';
    if (digitsTo.length !== 8) nextErrors.to_postal_code = 'CEP de destino deve ter 8 dígitos.';
    if (!Number.isFinite(weight) || weight <= 0) nextErrors.weight_g = 'Peso inválido.';
    if (!Number.isFinite(height) || height <= 0) nextErrors.height_cm = 'Altura inválida.';
    if (!Number.isFinite(width) || width <= 0) nextErrors.width_cm = 'Largura inválida.';
    if (!Number.isFinite(length) || length <= 0) nextErrors.length_cm = 'Comprimento inválido.';
    if (Object.values(nextErrors).some(Boolean)) {
      setFieldErrors(nextErrors);
      return;
    }

    if (!activeKey) {
      setFormError('Ainda estamos preparando sua chave de cotação. Tente novamente em instantes.');
      return;
    }

    setLoading(true);
    setQuote(null);
    try {
      const result = await postQuote(activeKey.key, {
        from_postal_code: digitsFrom,
        to_postal_code: digitsTo,
        packages: [{ weight_g: weight, height_cm: height, width_cm: width, length_cm: length }],
      });
      setQuote(result);
    } catch (err) {
      if (err instanceof ApiProblemError) {
        const problemFieldErrors: Record<string, string | undefined> = {};
        for (const fieldErr of err.problem.errors ?? []) {
          problemFieldErrors[fieldErr.field] = fieldErr.message;
        }
        if (Object.keys(problemFieldErrors).length > 0) {
          setFieldErrors(problemFieldErrors);
        } else {
          setFormError(err.problem.detail ?? err.problem.title);
        }
      } else {
        setFormError('Não foi possível cotar agora. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Cotação de frete</h1>
        <p className="text-sm text-muted-foreground">
          Simule uma cotação a qualquer momento e compare preço e prazo entre as transportadoras conectadas.
        </p>
      </div>

      {keyError ? (
        <Alert variant="error" title="Não foi possível preparar a cotação">
          {keyError}
        </Alert>
      ) : keyLoading ? (
        <LoadingState label="Preparando sua chave de cotação…" />
      ) : (
        <Alert variant={isSandbox ? 'warning' : 'info'} title={isSandbox ? 'Cotação simulada (sandbox)' : 'Cotação real'}>
          {isSandbox
            ? 'Você ainda não conectou nenhum provedor — esta cotação usa dados simulados, sem chamada real a nenhuma transportadora.'
            : 'Você tem pelo menos um provedor conectado — esta cotação chama a API real dos provedores conectados.'}
        </Alert>
      )}

      <Card className="p-6">
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor={fromId}>CEP de origem</Label>
            <Input
              id={fromId}
              inputMode="numeric"
              maxLength={9}
              placeholder="00000-000"
              value={fromPostalCode}
              disabled={loading}
              invalid={Boolean(fieldErrors.from_postal_code)}
              aria-describedby={fieldErrors.from_postal_code ? `${fromId}-error` : undefined}
              onChange={(event) => setFromPostalCode(event.target.value)}
            />
            <FieldError id={`${fromId}-error`} message={fieldErrors.from_postal_code} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={toId}>CEP de destino</Label>
            <Input
              id={toId}
              inputMode="numeric"
              maxLength={9}
              placeholder="00000-000"
              value={toPostalCode}
              disabled={loading}
              invalid={Boolean(fieldErrors.to_postal_code)}
              aria-describedby={fieldErrors.to_postal_code ? `${toId}-error` : undefined}
              onChange={(event) => setToPostalCode(event.target.value)}
            />
            <FieldError id={`${toId}-error`} message={fieldErrors.to_postal_code} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={weightId}>Peso (g)</Label>
            <Input
              id={weightId}
              inputMode="numeric"
              value={weightG}
              disabled={loading}
              invalid={Boolean(fieldErrors.weight_g)}
              aria-describedby={fieldErrors.weight_g ? `${weightId}-error` : undefined}
              onChange={(event) => setWeightG(event.target.value)}
            />
            <FieldError id={`${weightId}-error`} message={fieldErrors.weight_g} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={heightId}>Altura (cm)</Label>
            <Input
              id={heightId}
              inputMode="numeric"
              value={heightCm}
              disabled={loading}
              invalid={Boolean(fieldErrors.height_cm)}
              aria-describedby={fieldErrors.height_cm ? `${heightId}-error` : undefined}
              onChange={(event) => setHeightCm(event.target.value)}
            />
            <FieldError id={`${heightId}-error`} message={fieldErrors.height_cm} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={widthId}>Largura (cm)</Label>
            <Input
              id={widthId}
              inputMode="numeric"
              value={widthCm}
              disabled={loading}
              invalid={Boolean(fieldErrors.width_cm)}
              aria-describedby={fieldErrors.width_cm ? `${widthId}-error` : undefined}
              onChange={(event) => setWidthCm(event.target.value)}
            />
            <FieldError id={`${widthId}-error`} message={fieldErrors.width_cm} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={lengthId}>Comprimento (cm)</Label>
            <Input
              id={lengthId}
              inputMode="numeric"
              value={lengthCm}
              disabled={loading}
              invalid={Boolean(fieldErrors.length_cm)}
              aria-describedby={fieldErrors.length_cm ? `${lengthId}-error` : undefined}
              onChange={(event) => setLengthCm(event.target.value)}
            />
            <FieldError id={`${lengthId}-error`} message={fieldErrors.length_cm} />
          </div>

          <Button type="submit" variant="default" disabled={loading || keyLoading} className="col-span-full self-start sm:w-auto">
            {loading ? 'Cotando…' : 'Cotar'}
          </Button>
        </form>
      </Card>

      {formError ? (
        <Alert variant="error" title="Não foi possível cotar">
          {formError}
        </Alert>
      ) : null}

      {unmappedFieldErrors.length > 0 ? (
        <Alert variant="error" title="Não foi possível cotar">
          <ul className="list-disc pl-4">
            {unmappedFieldErrors.map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {loading ? <LoadingState label="Buscando ofertas…" /> : null}

      {quote ? <QuoteResult quote={quote} /> : null}
    </div>
  );
}
